<template>
    <ScrollableView class="preorder-time-component-container scrollable" contentClass="preorder-time-component">
        <header v-if="showHeader">
            <img v-if="hasLogo" class="logo" :src="logoPath" alt="Logo" />
            <h2 class="table-name">
                {{ tableName }}
            </h2>
        </header>

        <p v-if="showHeader && hasWelcomeMessage" class="description" v-text="message"/>

        <OrderDateTimeForm
            v-model="selectedOrderDateAndTime"
            ref="orderDateTimeFormComponent"
        />
    </ScrollableView>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import type { SelectedOrderDateAndTime } from "@/data/model/Customer";
    import type { IService } from "@/data/model/Service";
    import { useCartStore } from "@/data/stores/cart";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { exceptionTypes } from "@/data/util/Config";
    import { translate } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, onMounted, ref } from "vue";

    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import OrderDateTimeForm from "@/components/preorder/OrderDateTimeForm.vue";

    // setup store
    const tableAllocation = useTableAllocationStore();
    const cart = useCartStore();

    // define model
    const selectedOrderDateAndTime = defineModel<SelectedOrderDateAndTime | null>("selectedOrderDateAndTime", {
        required: true,
    });

    // define props
    const props = defineProps<{
        selectedServiceId: string | undefined | null;
        showHeader: boolean;
        initializeOnMounted: boolean;
    }>();

    // define emits
    const emit = defineEmits(["orderDateAndTimeSelected"]);

    // define template refs
    const orderDateTimeFormComponent = ref<InstanceType<typeof OrderDateTimeForm> | null>(null);

    // current service
    const currentService = computed<IService | null>(() => {
        return tableAllocation.allocation?.services.find((service) => service.id === props.selectedServiceId) ?? null;
    });

    // logo
    const logoPath = computed(() => tableAllocation.allocation?.locationDesign.logoDarkPath);
    const hasLogo = computed(() => logoPath.value != null);

    // table name
    const tableName = computed(
        () => `${tableAllocation.allocation?.locationName} - ${tableAllocation.allocation?.tableName}`
    );

    // message
    const message = computed(() => translate(currentService.value?.useCase?.beforeOrderPlacedText));
    const hasWelcomeMessage = computed(() => message.value != null);

    // define functions
    async function initialize() {
        // load current cart
        await cart.loadCurrentCart();

        // if exists, delete cart if it was for different service
        if (existsCartForDifferentService()) {
            try {
                // Currently we allow to have one cart per location
                await cart.deleteCartsFromLocation();
            } catch (ex) {
                logError(ex);
            }
        }

        // set initial selectedOrderDateAndTime
        if (hasCartWithPreorder()) {
            orderDateTimeFormComponent.value?.initialize({
                date: cart.currentCart!.preorderDate,
                time: cart.currentCart!.preorderTime,
                offerId: cart.currentCart!.offerId,
            } as SelectedOrderDateAndTime, props.selectedServiceId, null);
        } else {
            orderDateTimeFormComponent.value?.initialize(null, props.selectedServiceId, null);
        }
    }

    function existsCartForDifferentService() {
        if (cart.currentCart != null) {
            if (cart.currentCart.serviceId != props.selectedServiceId) {
                return true;
            }
            if (cart.orderService?.useCase?.usePreorder != true) {
                return true;
            }
        }
        return false;
    }

    function hasCartWithPreorder() {
        return (
            cart.currentCart != null && cart.currentCart.preorderDate != null && cart.currentCart.preorderTime != null
        );
    }

    async function selectOrderDateAndTime() {
        try {
            await cart.selectFulfillmentSlot(
                props.selectedServiceId!,
                selectedOrderDateAndTime.value!.date,
                selectedOrderDateAndTime.value!.time
            );

            emit("orderDateAndTimeSelected");
        } catch (ex) {
            logError(ex);
            const e = ex as any;
            if (e.type === exceptionTypes.selectedFulfillmentSlotNotAvailableException) {
                Popup.show({
                    message: translations.pay.fulfillmentSlotTimedOut,
                    confirmHandler: () => {
                        orderDateTimeFormComponent.value?.initialize(selectedOrderDateAndTime.value, props.selectedServiceId, null);
                    },
                    uniqueId: "selected-fulfillment-slot-not-available-popup",
                });
            } else if (e.type === exceptionTypes.noFulfillmentSlotsAvailableException) {
                Popup.show({
                    message: translations.pay.noFulfillmentSlotAvailableV2.replace(
                        "{0}",
                        tableAllocation.allocation?.locationName ?? ""
                    ),
                    confirmHandler: () => {
                        orderDateTimeFormComponent.value?.initialize(selectedOrderDateAndTime.value, props.selectedServiceId, null);
                    },
                    uniqueId: "no-fulfillment-slot-available-popup",
                });
            } else {
                Popup.show({
                    message: translations.cart.failedToChange, //TODO: To change to more generic
                    uniqueId: "unknown-error-popup",
                });
            }
        }
    }


    function reset() {
        orderDateTimeFormComponent.value?.reset();
    }

    onMounted(() => {
        if (props.initializeOnMounted)
            initialize();
    });

    // define exposed
    defineExpose({
        selectOrderDateAndTime,
        reset,
        initialize
    });
    // #endregion
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .preorder-time-component-container {
        flex: 1 1 auto;
    }

    .preorder-time-component {
        font-size: 16px;
        padding: 1.5em 2em;
        color: colors.$fontColor;

        > header {
            display: flex;
            align-items: center;
            margin-bottom: 1.25em;

            .logo {
                flex: 0 0 auto;
                max-width: 3.5em;
                max-height: 3.5em;
                margin-right: 1em;
            }

            h2 {
                flex: 1 1 auto;
                font-weight: 400;
                font-size: 1.25em;
                margin: 0;
            }
        }

        .description {
            font-weight: 400;
            font-size: 15px;
            text-align: justify;
            line-height: 1.5em;
            margin: 0;
            margin-bottom: 1.5em;
            white-space: pre-line;
        }
    }

    // kiosk: the block is anchored on one font size and lays itself out in em, so the
    // anchor carries the padding, the logo and the table name with it. the description
    // sets its own size in px and has to be scaled on its own
    .kiosk-mode .preorder-time-component {
        font-size: 20px * variables.$kioskScale !important;

        .description {
            font-size: 16px * variables.$kioskScale !important;;
        }
    }
</style>
